import { useRef, useState } from "react";
import { ArrowLeft, Loader2, Lock, Send, Unlock } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { nbBrand, useI18n } from "@/i18n/I18nContext";
import { usePrice, useEuro } from "@/components/market/Plans";
import { PlanBadge } from "@/components/admin/ConvPlanBar";
import { OfferCard, ThreadTypeBadge } from "@/components/offers/OfferCard";

const AUTHOR_STYLE = {
  dev: "bg-[rgba(245,197,66,0.1)] border-[rgba(245,197,66,0.3)]",
  admin: "bg-[rgba(179,20,28,0.18)] border-[rgba(179,20,28,0.45)]",
  system: "bg-black/40 border-neutral-700",
};

const Bubble = ({ m, admin, brand, renderAction, currency, onDiscuss, onReload }) => {
  const { t, fmtDate, locale } = useI18n();
  const n = (x) => new Intl.NumberFormat(locale).format(x);
  const { price, k } = usePrice(currency);
  const euro = useEuro();
  const mine = admin ? m.author === "admin" : m.author === "dev";
  const who = m.author === "system" ? t("msg.system") : mine ? t("msg.you") : m.author === "dev" ? brand : t("msg.team");
  return (
    <li className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-item-${m.id}`}>
      <div className={`max-w-[85%] min-w-0 rounded-2xl border p-3 ${AUTHOR_STYLE[m.author]}`}>
        <p className="text-[0.7rem] font-semibold uppercase tracking-wider text-neutral-400">{who} · {fmtDate(m.created_at)}</p>
        {m.action?.type === "offer_card" ? (
          <OfferCard a={{ ...m.action, created_at: m.action.created_at || m.created_at }} admin={admin} onDiscuss={onDiscuss} onPaid={onReload} testId={`msg-offer-${m.id}`} />
        ) : m.action?.type === "pay_custom" ? (
          <p className="mt-1 whitespace-pre-wrap break-words text-sm text-white" data-testid={`msg-body-${m.id}`}>
            {t(k("custom.offer"), { n: n(m.action.apps), price: price({ eur: m.action.eur }) })}
          </p>
        ) : (
          <p className="mt-1 whitespace-pre-wrap break-words text-sm text-white" data-testid={`msg-body-${m.id}`}>{nbBrand(m.body)}</p>
        )}
        {m.action?.type === "plan_request" && (
          <div className="mt-2 flex flex-wrap items-center gap-2 rounded-xl border border-fuchsia-400/40 bg-fuchsia-500/10 p-2 text-xs text-fuchsia-100" data-testid={`msg-plan-request-${m.id}`}>
            <PlanBadge />
            <bdi>{m.action.apps ? t(m.action.eur ? "msg.planReq" : "msg.planReqNoPrice", { n: n(m.action.apps), price: euro(m.action.eur || 0) }) : t("msg.planReqMini")}</bdi>
          </div>
        )}
        {m.action && m.action.type !== "offer_card" && renderAction?.(m)}
      </div>
    </li>
  );
};

export const Thread = ({ conv, admin, onSend, onStatus, onBack, renderAction, onReload }) => {
  const { t } = useI18n();
  const [text, setText] = useState("");
  const input = useRef(null);
  const discuss = () => {
    setText((v) => v || t("offer.discussPrefill"));
    input.current?.focus();
  };
  const [busy, setBusy] = useState(false);
  const closed = conv.status === "closed";
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      await onSend(text.trim());
      setText("");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex min-w-0 flex-col" data-testid="msg-thread">
      <div className="flex flex-wrap items-center gap-2 border-b border-neutral-800 p-4">
        <button type="button" onClick={onBack} className="rounded-full p-1.5 text-neutral-300 hover:bg-white/5 lg:hidden" aria-label={t("msg.back")} data-testid="msg-back-btn">
          <ArrowLeft className="h-4 w-4 rtl:-scale-x-100" />
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-white" data-testid="msg-thread-subject">{conv.subject}</p>
          <p className="text-xs text-neutral-400">
            {admin && conv.thread_type ? <ThreadTypeBadge type={conv.thread_type} testId="msg-thread-category" /> : conv.category === "custom" ? <PlanBadge testId="msg-thread-category" /> : <span className="text-[var(--gold-bright)]" data-testid="msg-thread-category">{t(`msg.cat.${conv.category}`)}</span>}
            {admin && <> · {conv.developer.brand} · <span className="break-all">{conv.developer.email}</span></>}
            {" · "}<span data-testid="msg-thread-status">{t(closed ? "msg.closed" : "msg.open")}</span>
          </p>
        </div>
        {admin && (
          <button type="button" className="btn-gold btn-sm" onClick={() => onStatus(closed ? "open" : "closed")} data-testid="msg-status-btn">
            {closed ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />} {t(closed ? "msg.reopen" : "msg.close")}
          </button>
        )}
      </div>
      <ul className="max-h-[55vh] space-y-3 overflow-y-auto p-4" data-testid="msg-items">
        {conv.messages.map((m) => <Bubble key={m.id} m={m} admin={admin} brand={conv.developer?.brand} renderAction={renderAction} currency={conv.billing_currency || undefined} onDiscuss={closed ? undefined : discuss} onReload={onReload} />)}
      </ul>
      {closed && !admin ? (
        <p className="border-t border-neutral-800 p-4 text-sm text-neutral-400" data-testid="msg-closed-note">{t("msg.closedNote")}</p>
      ) : (
        <form onSubmit={send} className="flex flex-col gap-2 border-t border-neutral-800 p-4 sm:flex-row sm:items-end">
          <Textarea ref={input} value={text} onChange={(e) => setText(e.target.value)} maxLength={5000} rows={3} placeholder={t("msg.replyPlaceholder")}
            className="min-w-0 flex-1 bg-black/50" data-testid="msg-reply-input" />
          <button type="submit" className="btn-red btn-sm" disabled={busy || !text.trim()} data-testid="msg-reply-send">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 rtl:-scale-x-100" />} {t("msg.send")}
          </button>
        </form>
      )}
    </div>
  );
};
